import { ChangeDetectorRef, Injectable, signal } from '@angular/core';
import { filter, Subject, take } from 'rxjs';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import { EventImpl, VerboseFormattingArg } from '@fullcalendar/core/internal';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGrid from '@fullcalendar/resource-timegrid';
import moment from 'moment/moment';
import { AbstractCalendarService } from '../../../core/services/AbstractCalendarService';
import { environment } from '../../../../environments/environment';
import { EventStatusTypes } from '../enums';
import { SwapMode } from '../types';

@Injectable()
export class SwapAuditoriumCalendarService extends AbstractCalendarService {

  public readonly leftColumnMode = signal<SwapMode>('neutral');
  public readonly rightColumnMode = signal<SwapMode>('neutral');
  public readonly selectedCount = signal(0);
  public leftAuditoriumId: number;
  public rightAuditoriumId: number;
  public readonly selectionChanged$ = new Subject<void>();

  public readonly calendarOptions: CalendarOptions = {
      plugins: [resourceTimeGrid, interactionPlugin],
      initialView: 'timeGridSevenDays',
      allDaySlot: false,
      datesAboveResources: true,
      schedulerLicenseKey: environment.fullcalendarLicenseKey,
      headerToolbar: false,
      footerToolbar: false,
      slotDuration: '00:05:00',
      slotLabelFormat: (arg: VerboseFormattingArg) => moment(arg.start).format('HH:mm'),
      slotMinTime: '08:00:00',
      slotMaxTime: '32:00:00',
      slotLabelInterval: { hours: 1 },
      editable: false,
      selectable: false,
      locale: this.locales[this.languageStoreService.language.token].default,
      views: {
          timeGridSevenDays: {
              type: 'resourceTimeGrid',
          },
      },
      visibleRange: {
          start: moment().format('YYYY-MM-DD'),
          end: moment().add(1, 'day').format('YYYY-MM-DD')
      },
      eventDurationEditable: false,
      eventStartEditable: false,
      eventDidMount: () => {
        this.scrollToFirstEvent()
      }
  };

  protected readonly unselectElementsSelector = 'fc-timegrid-slot';

  public constructor(
  ) {
      super();
  }

  public override init(
    fullCalendarComponent: FullCalendarComponent,
    parentCdr: ChangeDetectorRef,
    cb?: () => void
  ): void {
    super.init(fullCalendarComponent, parentCdr, () => {
      cb?.();
      this.areaSelectDirectiveInit$.pipe(filter(Boolean), take(1)).subscribe(() => {
        this.subs$.push(this.areaSelectDirective.onSelect.subscribe(() => {
          queueMicrotask(() => {
            this.syncSwapModesFromSelection();
            this.selectionChanged$.next();
          });
        }));
      });
    });
  }

  public setAuditoriumIds(left: number, right: number): void {
    this.leftAuditoriumId = left;
    this.rightAuditoriumId = right;
  }

  public getEventResourceId(event: EventImpl): string {
    return event.getResources()[0]?.id ?? event._def.resourceIds?.[0];
  }

  public getColumnModeForEvent(event: EventImpl): SwapMode {
    const resourceId = this.getEventResourceId(event);
    const leftAuditoriumIdStr = this.leftAuditoriumId?.toString();
    const rightAuditoriumIdStr = this.rightAuditoriumId?.toString();

    if (resourceId === leftAuditoriumIdStr) {
      return this.leftColumnMode();
    }

    if (resourceId === rightAuditoriumIdStr) {
      return this.rightColumnMode();
    }

    return 'neutral';
  }

  public isEventBlockedByColumnMode(event: EventImpl, columnMode: SwapMode): boolean {
    if (columnMode === 'neutral') {
      return false;
    }

    const isMarathon = !!event.extendedProps['eventProps']?.isMarathon;

    return columnMode === 'marathon' ? !isMarathon : isMarathon;
  }

  public isEventSwapTypeBlocked(event: EventImpl): boolean {
    return this.isEventBlockedByColumnMode(event, this.getColumnModeForEvent(event));
  }

  public getSwapTypeBlockedTooltip(event: EventImpl): string {
    const columnMode = this.getColumnModeForEvent(event);

    if (columnMode === 'marathon') {
      return 'Only double feature / marathon shows can be selected in this auditorium column.'
        .trans('planning/modals/swap-auditoriums-modal/swap-type-blocked-marathon.Tooltip');
    }

    if (columnMode === 'solo') {
      return 'Only single-film shows can be selected in this auditorium column.'
        .trans('planning/modals/swap-auditoriums-modal/swap-type-blocked-solo.Tooltip');
    }

    return '';
  }

  public syncSwapModesFromSelection(): void {
    const leftAuditoriumIdStr = this.leftAuditoriumId?.toString();
    const rightAuditoriumIdStr = this.rightAuditoriumId?.toString();
    const selected = this.getSelectedEvents() ?? [];

    const leftSelected = selected.filter(event => this.getEventResourceId(event) === leftAuditoriumIdStr);
    const rightSelected = selected.filter(event => this.getEventResourceId(event) === rightAuditoriumIdStr);

    this.leftColumnMode.set(this.resolveColumnMode(leftSelected));
    this.rightColumnMode.set(this.resolveColumnMode(rightSelected));

    this.deselectIncompatibleEvents(leftAuditoriumIdStr, this.leftColumnMode());
    this.deselectIncompatibleEvents(rightAuditoriumIdStr, this.rightColumnMode());

    const updatedSelected = this.getSelectedEvents() ?? [];
    const updatedLeftSelected = updatedSelected.filter(event => this.getEventResourceId(event) === leftAuditoriumIdStr);
    const updatedRightSelected = updatedSelected.filter(event => this.getEventResourceId(event) === rightAuditoriumIdStr);

    this.leftColumnMode.set(this.resolveColumnMode(updatedLeftSelected));
    this.rightColumnMode.set(this.resolveColumnMode(updatedRightSelected));
    this.refreshSelectedCount();
    this.applySwapModeClasses();
  }

  public applySwapModeClasses(): void {
    if (!this.fullCalendarApi) {
      return;
    }

    const events = this.fullCalendarApi.getEvents();

    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const columnMode = this.getColumnModeForEvent(event);
      const isBlocked = this.isEventBlockedByColumnMode(event, columnMode);
      const eventProps = event.extendedProps['eventProps'];
      const preservedClasses = [
        `auditorium-swap-event-${ eventProps?.showId }`,
        `status-${ eventProps?.status }`,
      ];
      const classNames = [
        ...preservedClasses,
        ...(isBlocked ? ['swap-type-blocked'] : ['selectable-event']),
      ];

      event.setProp('classNames', classNames);
    }
  }

  public resetSwapModes(): void {
    this.leftColumnMode.set('neutral');
    this.rightColumnMode.set('neutral');
    this.refreshSelectedCount();
    this.applySwapModeClasses();
  }

  public override getSelectedEvents(statusTypes: EventStatusTypes[] = null): EventImpl[] {
      return this.fullCalendarApi?.getEvents().filter(event => (
          !event.extendedProps['loading'] &&
          event.extendedProps['isSelected'] &&
          (statusTypes ? statusTypes.includes(event.extendedProps['eventProps']['status']) : true)
      ));
  }

  private deselectIncompatibleEvents(auditoriumIdStr: string, columnMode: SwapMode): void {
    if (!auditoriumIdStr || columnMode === 'neutral' || !this.fullCalendarApi) {
      return;
    }

    const deselect = (): void => {
      const events = this.fullCalendarApi.getEvents();

      for (let i = 0; i < events.length; i++) {
        const event = events[i];

        if (this.getEventResourceId(event) !== auditoriumIdStr || !event.extendedProps['isSelected']) {
          continue;
        }

        if (this.isEventBlockedByColumnMode(event, columnMode)) {
          event.setExtendedProp('isSelected', false);
        }
      }
    };

    if (typeof this.fullCalendarApi.batchRendering === 'function') {
      this.fullCalendarApi.batchRendering(deselect);
    } else {
      deselect();
    }
  }

  private resolveColumnMode(selectedEvents: EventImpl[]): SwapMode {
    if (!selectedEvents.length) {
      return 'neutral';
    }

    return selectedEvents[0].extendedProps['eventProps']?.isMarathon ? 'marathon' : 'solo';
  }

  private refreshSelectedCount(): void {
    this.selectedCount.set((this.getSelectedEvents() ?? []).length);
  }

  private scrollToFirstEvent(): void {
    this.fullCalendarEl.querySelector('div[id^="auditorium-swap-event-"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  public override ngOnDestroy(): void {
    this.areaSelectDirectiveInit$.next(false);
    super.ngOnDestroy();
  }
}
